import {useState} from 'react';
import {AnchorRect} from '../../domain/commentAnchor';
import {Size} from '../../domain/zoomPan';

/** The picture the comments are on, for anchoring them to its regions and previewing those. */
export interface CommentedPicture {
    /** An image of it already loaded (the viewer's), cropped for the threads' region previews. */
    url: string | undefined;
    /** Its displayed size, for the shape of a region's preview; null when unknown. */
    size: Size | null;
}

/**
 * Regions of a picture that comments refer to, shared by the comments panel and the picture: the
 * composer's draw button lets the user drag a rectangle on the picture, which the next new comment is
 * anchored to; a thread's button shows its region on the picture.
 */
export interface CommentAnchoring {
    /** The picture waits for a rectangle to be dragged on it. */
    drawing: boolean;
    /** The rectangle drawn for the comment being written. */
    draft: AnchorRect | null;
    /** The thread whose region is shown on the picture. */
    shownCommentId: string | null;
    startDrawing: () => void;
    cancelDrawing: () => void;
    finishDrawing: (rect: AnchorRect) => void;
    clearDraft: () => void;
    toggleShown: (commentId: string) => void;
    showRegion: (commentId: string) => void;
}

interface AnchoringState {
    fileId: string;
    drawing: boolean;
    draft: AnchorRect | null;
    shownCommentId: string | null;
}

/** The anchoring state of the photo `fileId`; another photo starts without any. */
export function useCommentAnchoring(fileId: string): CommentAnchoring {
    const initial: AnchoringState = {fileId, drawing: false, draft: null, shownCommentId: null};
    const [stored, setState] = useState(initial);
    const state = stored.fileId === fileId ? stored : initial;
    if (stored.fileId !== fileId) setState(initial);

    // From the latest state, and only while it is still this photo's: e.g. a draft cleared once its
    // comment is saved, after the user may have moved on.
    const update = (patch: (current: AnchoringState) => Partial<AnchoringState>) =>
        setState((current) => current.fileId === fileId ? {...current, ...patch(current)} : current);
    return {
        drawing: state.drawing,
        draft: state.draft,
        shownCommentId: state.shownCommentId,
        startDrawing: () => update(() => ({drawing: true})),
        cancelDrawing: () => update(() => ({drawing: false})),
        finishDrawing: (rect) => update(() => ({drawing: false, draft: rect})),
        clearDraft: () => update(() => ({draft: null})),
        toggleShown: (commentId) => update((current) => ({
            shownCommentId: current.shownCommentId === commentId ? null : commentId,
        })),
        showRegion: (commentId) => update(() => ({shownCommentId: commentId})),
    };
}
